import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { DownArrow, UpArrow } from 'icons';
import { useMemo } from 'react';
import {
  APTAMetricsByServiceMetric,
  NoiseReductionByServiceMetric,
  NoiseReductionByTeamMetric,
  OnCallUsersPerHourData,
} from '../api/types';
import { SortByAscendingIcon, SortByDescendingIcon } from '../icons/sorting';
import { AssigneeType } from 'library/enums';
import { AssigneeAvatar } from 'library/atoms/AssigneeAvatar';
import { getAssigneeComponent } from './helpers';
const SortingIcon = {
  asc: <SortByDescendingIcon />,
  desc: <SortByAscendingIcon />,
};

export const useAnalyticsTable = () => {
  const APTATableColumns = useMemo<ColumnDef<APTAMetricsByServiceMetric>[]>(
    () => [
      {
        id: 'serviceName',
        accessorKey: 'serviceName',
        header: ({ column }) => (
          <Box display="flex" alignItems="center" columnGap="3px" cursor="pointer">
            Service Name
          </Box>
        ),
        cell: ({ row }) => <Text>{row.original.serviceName}</Text>,
        enableSorting: false,
      },
      {
        id: 'incidentCount',
        accessorKey: 'totalIncidentCount',
        header: ({ column }) => (
          <Box
            display="flex"
            alignItems="center"
            columnGap="3px"
            cursor="pointer"
            sx={{ svg: { path: { fill: '#627C98' } } }}
          >
            Incident Count {SortingIcon[column.getIsSorted() as 'asc' | 'desc']}
          </Box>
        ),
        enableSorting: true,
        cell: ({ row }) => <Text>{row.original.totalIncidentCount}</Text>,
      },
      {
        id: 'aptaSuccessCount',
        accessorKey: 'aptaMarkedCount',
        header: ({ column }) => (
          <Box
            display="flex"
            alignItems="center"
            columnGap="3px"
            cursor="pointer"
            sx={{ svg: { path: { fill: '#627C98' } } }}
          >
            Transient Alerts {SortingIcon[column.getIsSorted() as 'asc' | 'desc']}
          </Box>
        ),
        enableSorting: true,
        cell: ({ row }) => <Text>{row.original.aptaMarkedCount}</Text>,
      },
    ],
    [],
  );

  const NoiseReductionTableColumns = useMemo<ColumnDef<NoiseReductionByServiceMetric>[]>(
    () => [
      {
        id: 'serviceName',
        accessorKey: 'serviceName',
        header: ({ column }) => <Box cursor="pointer">Service Name</Box>,
        cell: ({ row }) => <Text>{row.original.serviceName}</Text>,
        enableSorting: false,
      },
      {
        id: 'reductionPercentage',
        accessorKey: 'reductionPercentage',
        header: ({ column }) => (
          <Box
            display="flex"
            alignItems="center"
            columnGap="3px"
            cursor="pointer"
            sx={{ svg: { path: { fill: '#627C98' } } }}
          >
            Noise Reduction% {SortingIcon[column.getIsSorted() as 'asc' | 'desc']}
          </Box>
        ),
        enableSorting: true,
        cell: ({ row }) => <Text>{row.original.reductionPercentage} %</Text>,
      },
    ],
    [],
  );

  const NoiseReductionTeamTableColumns = useMemo<ColumnDef<NoiseReductionByTeamMetric>[]>(
    () => [
      {
        id: 'teamName',
        accessorKey: 'teamName',
        header: ({ column }) => <Box cursor="pointer">Team Name</Box>,
        cell: ({ row }) => <Text>{row.original.teamName}</Text>,
        enableSorting: false,
      },
      {
        id: 'reductionPercentage',
        accessorKey: 'reductionPercentage',
        header: ({ column }) => (
          <Box
            display="flex"
            alignItems="center"
            columnGap="3px"
            cursor="pointer"
            sx={{ svg: { path: { fill: '#627C98' } } }}
          >
            Noise Reduction% {SortingIcon[column.getIsSorted() as 'asc' | 'desc']}
          </Box>
        ),
        enableSorting: true,
        sortDescFirst: true,
        cell: ({ row }) => <Text>{row.original.reductionPercentage} %</Text>,
      },
    ],
    [],
  );

  const OnCallUsersPerHourColumns = useMemo<ColumnDef<OnCallUsersPerHourData>[]>(
    () => [
      {
        id: 'userName',
        accessorKey: 'userName',
        header: ({ column }) => <Box cursor="pointer">Name</Box>,
        cell: ({ row }) => (
          <>
            <HStack gap={2}>
              <AssigneeAvatar name={row.original.fullName} type={AssigneeType.User} />
              {getAssigneeComponent(
                row.original.userID,
                AssigneeType.User,
                row.original.fullName,
                30,
              )}
            </HStack>
            <Box style={{ marginLeft: 41.5, cursor: 'pointer' }}>
              <Text variant="body" color={'secondary.400'}>
                {row.original.userName}
              </Text>
            </Box>
          </>
        ),
      },
      {
        id: 'onCallHours',
        accessorKey: 'on Call Hours',
        enableSorting: false,
        cell: ({ row }) => <Text>{row.original.originalDuration} hrs</Text>,
      },
      {
        id: 'overrideHours',
        accessorKey: 'override Hours',
        enableSorting: false,
        cell: ({ row }) => <Text>{row.original.overrideDuration} hrs</Text>,
      },
    ],
    [],
  );

  return {
    APTATableColumns,
    NoiseReductionTableColumns,
    NoiseReductionTeamTableColumns,
    OnCallUsersPerHourColumns,
  };
};
